/**
 * One bulk delete for every read-only grid that deletes selected rows through
 * a batch route (the expenses grid, the journal grids). The route runs the
 * SAME single-row delete RPC per id, one at a time, and answers one flat
 * result per id; every rule lives in that RPC (docs/BACKEND_API.md).
 *
 * This hook owns the client half, once:
 *  - the queue (`runBatchQueue`): requests of at most `route.maxIds`,
 *    `not_attempted` ids re-sent, a failed request stamps the rest
 *    `request_failed` and sets `outcomeUnknown`;
 *  - the ONE Sentry capture per run (a whole-request failure);
 *  - the toasts, aggregated per run: one per DISTINCT refusal text (a
 *    declared-VAT refusal as the localized period sentence when the caller
 *    names a key), one success count, a warning when the outcome is unknown;
 *  - breadcrumbs, and ONE cache invalidation (also on an unknown outcome —
 *    some rows may have gone).
 *
 * A plain async `run`, not `useMutation`: per-row refusals resolve, never
 * reject. Callers stay thin — `useBulkDeleteExpenses` pre-skips declared
 * rows, `useBulkDeleteJournals` sends everything.
 *
 * Generic over the route's SCOPE: the org-scoped grids use `Scope = string`
 * (the organization id — the default, so their configs name no type), the
 * user-scoped inbox uses `Scope = void`. The scope reaches the breadcrumbs and
 * the Sentry capture as `organizationId` only when it is a string.
 */
import { useCallback, useRef, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import * as Sentry from '@sentry/react';
import { VAT_DECLARED } from '@releve/shared';
import { toast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { authedPost } from '@/lib/api';
import { safeFormatDate, getDateFnsLocale } from '@/lib/dateUtils';
import { runBatchQueue, toastDistinctErrors } from '@/lib/batchQueue';
import { addBreadcrumb } from '@/lib/sentry';

/** Transport codes the bulk delete stamps on a row itself — never an RPC outcome. */
export const BULK_DELETE_REQUEST_FAILED = 'request_failed';
export const BULK_DELETE_NOT_ATTEMPTED = 'not_attempted';

/** The flat per-id result every batch-delete route answers (plus its id field). */
export interface BulkDeleteResultBase {
  ok: boolean;
  error?: string;
  code?: string;
  vat_settlement_id?: string;
  period_start?: string | null;
  period_end?: string | null;
}

export interface BulkDeleteConfig<Scope = string> {
  /** Sentry `feature` tag + breadcrumb prefix ("expenses", "journals"). */
  feature: string;
  /**
   * Extra Sentry tags on the whole-request failure capture — each caller keeps
   * its own action tag (`expense_action: 'bulk-delete'`) so saved searches
   * and alerts keep matching.
   */
  sentryTags: Record<string, string>;
  route: {
    path: (scope: Scope) => string;
    /** Request body key carrying the id chunk (`expense_ids`). */
    idsKey: string;
    /** Result key carrying the row's id (`expense_id`). */
    idField: string;
    /** The route's own per-request limit. */
    maxIds: number;
  };
  /** ONE refresh for the whole run. */
  invalidate: (queryClient: QueryClient, scope: Scope) => void;
  /** i18n keys (already namespaced). */
  i18n: {
    /** Plural `count` — "Deleted N …". */
    done: string;
    /** Fallback text for a refusal without a message. */
    rowFailed: string;
    outcomeUnknown: string;
    /**
     * Optional `{{start}} – {{end}}` period sentence for a declared-VAT
     * refusal; without it the server's own message is shown.
     */
    vatDeclared?: string;
  };
}

export interface BulkDeleteOutcome<R extends BulkDeleteResultBase> {
  /** Deleted. */
  done: string[];
  /** Refused or failed — the row stays selected. */
  failed: Array<{ id: string; result: R }>;
  /** A request failed after it may have reached the server. */
  outcomeUnknown: boolean;
}

export interface RunBulkDeleteOptions {
  /** Rows with a final result so far. */
  onProgress?: (done: number, total: number) => void;
}

/**
 * The declared-VAT refusal as a sentence naming the filed period in the
 * user's language (date-fns `PP` = localized medium date, so the NL/FR
 * sentence does not carry an English month).
 */
export function formatVatDeclaredMessage(
  key: string,
  refusal: { period_start?: string | null; period_end?: string | null },
  t: TFunction,
  language: string,
): string {
  const locale = getDateFnsLocale(language);
  return t(key, {
    start: safeFormatDate(refusal.period_start ?? null, 'PP', '—', locale),
    end: safeFormatDate(refusal.period_end ?? null, 'PP', '—', locale),
  });
}

/** `{ organizationId }` for an org scope, nothing otherwise (breadcrumb / Sentry extra). */
function orgContextOf(scope: unknown): { organizationId?: string } {
  return typeof scope === 'string' ? { organizationId: scope } : {};
}

export function useBulkDelete<R extends BulkDeleteResultBase, Scope = string>(
  config: BulkDeleteConfig<Scope>,
) {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const [isRunning, setIsRunning] = useState(false);
  // Callers pass a static config; the ref keeps `run` stable regardless.
  const configRef = useRef(config);
  configRef.current = config;

  const run = useCallback(
    async (
      scope: Scope,
      ids: ReadonlyArray<string>,
      { onProgress }: RunBulkDeleteOptions = {},
    ): Promise<BulkDeleteOutcome<R>> => {
      const { feature, route, invalidate, i18n: keys } = configRef.current;
      const outcome: BulkDeleteOutcome<R> = { done: [], failed: [], outcomeUnknown: false };
      if (ids.length === 0) return outcome;

      addBreadcrumb(`${feature}.bulkDelete: started`, { count: ids.length, ...orgContextOf(scope) });

      setIsRunning(true);
      onProgress?.(0, ids.length);
      try {
        const path = route.path(scope);
        const batch = await runBatchQueue<string, { results?: R[] } | undefined, R>({
          items: ids,
          chunkSize: route.maxIds,
          send: (chunk) =>
            authedPost<{ results?: R[] } | undefined>(path, { [route.idsKey]: chunk }),
          resultsOf: (response) => response?.results,
          isNotAttempted: (r) => !r.ok && r.code === BULK_DELETE_NOT_ATTEMPTED,
          failRemaining: (id, error) =>
            ({
              [route.idField]: id,
              ok: false,
              code: BULK_DELETE_REQUEST_FAILED,
              error: error.message,
            }) as unknown as R,
          onRequestFailed: (error, { sentCount, total }) => {
            Sentry.withScope((sentryScope) => {
              sentryScope.setTag('feature', feature);
              for (const [key, value] of Object.entries(configRef.current.sentryTags)) {
                sentryScope.setTag(key, value);
              }
              const { organizationId } = orgContextOf(scope);
              if (organizationId) sentryScope.setExtra('organizationId', organizationId);
              sentryScope.setExtra('idCount', total);
              sentryScope.setExtra('sentCount', sentCount);
              Sentry.captureException(error);
            });
          },
          onProgress,
          label: `${feature} batch delete`,
        });
        outcome.outcomeUnknown = batch.outcomeUnknown;
        batch.results.forEach((result, i) => {
          if (result.ok) outcome.done.push(ids[i]);
          else outcome.failed.push({ id: ids[i], result });
        });
      } finally {
        setIsRunning(false);
      }

      if (outcome.done.length > 0 || outcome.outcomeUnknown) {
        invalidate(queryClient, scope);
      }

      // A whole-request failure gets the outcome-unknown warning below, not a
      // toast per row.
      toastDistinctErrors(
        outcome.failed
          .filter(({ result }) => result.code !== BULK_DELETE_REQUEST_FAILED)
          .map(({ result }) =>
            result.code === VAT_DECLARED && keys.vatDeclared
              ? formatVatDeclaredMessage(keys.vatDeclared, result, t, i18n.language)
              : result.error || t(keys.rowFailed),
          ),
      );

      if (outcome.done.length > 0) {
        toast.success(t(keys.done, { count: outcome.done.length }));
      }
      if (outcome.outcomeUnknown) {
        toast.warning(t(keys.outcomeUnknown));
      }

      addBreadcrumb(
        `${feature}.bulkDelete: finished`,
        {
          done: outcome.done.length,
          failed: outcome.failed.length,
          outcomeUnknown: outcome.outcomeUnknown,
        },
        outcome.failed.length > 0 ? 'warning' : 'info',
      );

      return outcome;
    },
    [queryClient, t, i18n.language],
  );

  return { run, isRunning };
}
